// src\app\page\signup-page\signup-page.component.ts
import { AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AppConstants } from 'src/app/constants/app-constants';
import { SignupEntry } from 'src/app/model/SignupEntry';
import { SignupRecord } from 'src/app/model/SignupRecord';
import { SignupService } from 'src/app/services/signup/signup.service';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { confirmPasswordMatchValidator, customPasswordValidator } from 'src/app/shared/password.validator';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

/** Definition of the form using custom definitions */
@Component({
    selector: 'app-signup-page',
    templateUrl: './signup-page.component.html',
    styleUrls: ['./signup-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignupPageComponent implements OnInit, OnDestroy {
  /** Injected dependencies */
  private _userService: UserService = inject(UserService);
  private _router: Router = inject(Router);
  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _signupService: SignupService = inject(SignupService);
  private _authService: AuthService = inject(AuthService);
  private _toastrService: ToastrService = inject(ToastrService);

  /** Signup form group */
  public signupForm: FormGroup = new FormGroup({
    email: new FormControl("", [Validators.required, Validators.email]),
    password: new FormControl("", [customPasswordValidator]),
    confirmPassword: new FormControl("", [Validators.required, confirmPasswordMatchValidator("password")]),
  });

  /** Signup record object */
  public signupRecord: SignupRecord | null = null;

  /** Component state flags */
  public isLoading: boolean = true;
  public isSubmitting: boolean = false;

  /** Error message */
  public errorMessage: string = "";

  /** Destroy subscription */
  private _destroy$ = new Subject<void>();

  /**
   * When the component is first initalized, check if they're authenticated and if their input is valid
   */
  ngOnInit(): void {
    // If authenticated navigate home
    if (this._authService.authenticated) this._router.navigate([this._userService.defaultNavPath]);

    // Find the signup GUID from the URL
    this._route.paramMap.pipe(
      takeUntil(this._destroy$)
    ).subscribe((params: ParamMap) => {
      // If no guid provided, navigate home
      const guid = params.get("signupGuid") || "";
      if (guid === "") {
        this._router.navigate([AppConstants.MENU_LOGIN]);
        return;
      }

      // Get the signup entry associated with the provided GUID
      this._signupService.getSignupRecord(guid).pipe(
        take(1),
        finalize(() => this.isLoading = false),
      ).subscribe({
        next: (record: SignupRecord | null): void => {
          // If no record is found, navigate to login
          if (record === null) {
            this.errorMessage = "The signup link is invalid or has expired.";
            return;
          }

          // Update the signup record and email field
          this.signupRecord = record;
          this.signupForm.controls["email"].setValue(record.email);
        },
        error: (error: HttpErrorResponse) => {
          // Handle signup record get errors
          switch (error.status) {
            case 409: // Conflict - already signed up
              this.errorMessage = "This signup link has already been used. Please log in.";
              break;
            case 400: // Bad Request - invalid or expired, display readable message
              const msg = error.error?.message || "The signup link is invalid or has expired.";
              this.errorMessage = msg;
              break;
            default: // Other error
              this.errorMessage = "An error occurred. Please try again later.";
              break;
          }
        }
      });
      
    });
  }

  /**
   * On destroy emit the destroy subject
   */
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Signup a new user
   */
  public signup(): void {
    // If the signup record is null, don't log in
    if (this.signupRecord == undefined) return;

    // If the user clicked the signup button, don't let them click again
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Create a signup entry
    const signupEntry: SignupEntry = {
      email: this.signupForm.value.email,
      password: this.signupForm.value.password,
      applicationGuid: this.signupRecord!.applicationGuid,
      signupGuid: this.signupRecord!.signupGuid,
    };

    this._signupService.signup(signupEntry).pipe(
      finalize(() => setTimeout(() => this.isSubmitting = false, 500)),
    ).subscribe({
      next: () => {
        // Navigate to the appropriate page after signup
        this._toastrService.success("Signup successful!");
        this._router.navigate([AppConstants.MENU_LOGIN]);
      },
      error: (error) => {
        // Handle error
        console.error("[SIGNUP-PAGE]: signup failed:", error);
        this._toastrService.error("Signup failed. Please try again.");
      }
    });
  }
}
